import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Edit, Trash2, Shield, User as UserIcon, Upload, Database, BarChart3, Phone, Settings, Key, Mail, X, Clock, Copy, Check, ToggleLeft, ToggleRight, Building2, DollarSign, ExternalLink, Send, Brain, Network, Landmark } from 'lucide-react';
import adminService from '../services/adminService';
import UserModal from '../components/UserModal';
import ConfirmDialog from '../components/ConfirmDialog';
import Import from '../components/Import';
import WebsiteAnalytics from '../components/admin/WebsiteAnalytics';
import RingCentralIntegration from '../components/admin/RingCentralIntegration';
import UserManagementPanel from '../components/admin/UserManagementPanel';
import AITrainingPanel from '../components/admin/AITrainingPanel';
import ActivityTrackingPanel from '../components/admin/ActivityTrackingPanel';
import MeshNetworkPanel from '../components/admin/MeshNetworkPanel';
import TelnyxConfig from '../components/admin/TelnyxConfig';
import CourtRecordsAdmin from '../components/admin/CourtRecordsAdmin';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('users');
  const [phoneSubTab, setPhoneSubTab] = useState('telnyx');
  const [systemSettings, setSystemSettings] = useState({
    mail_mode: 'client',
    calendar_mode: 'client'
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Password reset state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Invite state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('client');
  const [invitations, setInvitations] = useState([]);
  const [copiedInviteUrl, setCopiedInviteUrl] = useState(null);

  // User management panel state
  const [managedUserId, setManagedUserId] = useState(null);

  // Firm settings state
  const [officeHours, setOfficeHours] = useState({
    monday: { open: '08:00', close: '17:00', is_open: true },
    tuesday: { open: '08:00', close: '17:00', is_open: true },
    wednesday: { open: '08:00', close: '17:00', is_open: true },
    thursday: { open: '08:00', close: '17:00', is_open: true },
    friday: { open: '08:00', close: '17:00', is_open: true },
    saturday: { open: '09:00', close: '12:00', is_open: false },
    sunday: { open: '00:00', close: '00:00', is_open: false }
  });
  const [firmSettingsLoading, setFirmSettingsLoading] = useState(false);

  // Away message state
  const [awayMessage, setAwayMessage] = useState({
    enabled: false,
    subject: 'Thank you for contacting us',
    message: '',
    cooldown_hours: 24,
    exclude_domains: [],
    exclude_patterns: []
  });

  // Intake settings state
  const [intakeSettings, setIntakeSettings] = useState({
    default_consult_fee: 15000  // $150 in cents
  });

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
      loadInvitations();
    } else if (activeTab === 'settings') {
      loadSystemSettings();
    } else if (activeTab === 'firm') {
      loadFirmSettings();
    }
  }, [activeTab]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);


  const loadSystemSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/system/settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSystemSettings({
          mail_mode: data.mail_mode?.value || 'client',
          calendar_mode: data.calendar_mode?.value || 'client'
        });
      }
      setError('');
    } catch (err) {
      setError('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSystemSetting = async (key, value) => {
    try {
      const response = await fetch(`/api/system/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value })
      });
      if (response.ok) {
        setSystemSettings(prev => ({ ...prev, [key]: value }));
        setError('');
      } else {
        throw new Error('Failed to update setting');
      }
    } catch (err) {
      setError('Failed to update setting: ' + err.message);
    }
  };

  const loadFirmSettings = async () => {
    try {
      setFirmSettingsLoading(true);
      const token = localStorage.getItem('token');

      // Load office hours
      const officeResponse = await fetch('/api/firm-settings/office_hours', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (officeResponse.ok) {
        const data = await officeResponse.json();
        if (data.value) {
          setOfficeHours(data.value);
        }
      }

      // Load away message settings
      const awayResponse = await fetch('/api/firm-settings/away_message', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (awayResponse.ok) {
        const data = await awayResponse.json();
        if (data.value) {
          setAwayMessage(data.value);
        }
      }

      // Load intake settings (consultation fee)
      const intakeResponse = await fetch('/api/firm-settings/intake_settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (intakeResponse.ok) {
        const data = await intakeResponse.json();
        if (data.value) {
          setIntakeSettings(data.value);
        }
      }

      setError('');
    } catch (err) {
      setError('Failed to load firm settings');
    } finally {
      setFirmSettingsLoading(false);
    }
  };

  const saveOfficeHours = async () => {
    try {
      setFirmSettingsLoading(true);
      const response = await fetch('/api/firm-settings/office_hours', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: officeHours })
      });
      if (response.ok) {
        setSuccessMessage('Office hours saved successfully');
        setError('');
      } else {
        throw new Error('Failed to save office hours');
      }
    } catch (err) {
      setError('Failed to save office hours: ' + err.message);
    } finally {
      setFirmSettingsLoading(false);
    }
  };

  const handleOfficeHoursChange = (day, field, value) => {
    setOfficeHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const saveAwayMessage = async () => {
    try {
      setFirmSettingsLoading(true);
      const response = await fetch('/api/firm-settings/away_message', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: awayMessage })
      });
      if (response.ok) {
        setSuccessMessage('Away message settings saved successfully');
        setError('');
      } else {
        throw new Error('Failed to save away message settings');
      }
    } catch (err) {
      setError('Failed to save away message settings: ' + err.message);
    } finally {
      setFirmSettingsLoading(false);
    }
  };

  const handleAwayMessageChange = (field, value) => {
    setAwayMessage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveIntakeSettings = async () => {
    try {
      setFirmSettingsLoading(true);
      const response = await fetch('/api/firm-settings/intake_settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: intakeSettings })
      });
      if (response.ok) {
        setSuccessMessage('Intake settings saved successfully');
        setError('');
      } else {
        throw new Error('Failed to save intake settings');
      }
    } catch (err) {
      setError('Failed to save intake settings: ' + err.message);
    } finally {
      setFirmSettingsLoading(false);
    }
  };

  const handleIntakeSettingsChange = (field, value) => {
    setIntakeSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllUsers();
      setUsers(data);
      setError('');
    } catch (err) {
      setError('Failed to load users: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      const data = await adminService.getInvitations();
      setInvitations(data.invitations || []);
    } catch (err) {
      console.error('Failed to load invitations:', err);
    }
  };

  // Password reset handlers
  const openPasswordModal = (user) => {
    setUserToReset(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const handleResetPassword = async () => {
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPasswordError('Password must contain an uppercase letter');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setPasswordError('Password must contain a lowercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPasswordError('Password must contain a number');
      return;
    }

    try {
      await adminService.resetUserPassword(userToReset.id, newPassword);
      setIsPasswordModalOpen(false);
      setSuccessMessage(`Password reset for ${userToReset.username}`);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Invite handlers
  const openInviteModal = () => {
    setInviteEmail('');
    setInviteRole('client');
    setIsInviteModalOpen(true);
  };

  const handleSendInvite = async () => {
    try {
      const result = await adminService.inviteUser(inviteEmail, inviteRole);
      setIsInviteModalOpen(false);
      setSuccessMessage(`Invitation sent to ${inviteEmail}`);
      loadInvitations();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      await adminService.revokeInvitation(invitationId);
      setSuccessMessage('Invitation revoked');
      loadInvitations();
    } catch (err) {
      setError('Failed to revoke invitation');
    }
  };

  const handleResendInvitation = async (invitationId, email) => {
    try {
      const response = await fetch(`/api/users/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        setSuccessMessage(`Invitation resent to ${email}`);
        loadInvitations();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to resend invitation');
      }
    } catch (err) {
      setError('Failed to resend invitation');
    }
  };

  const copyInviteUrl = async (invite) => {
    const url = `/accept-invite?token=${invite.token}`;
    await navigator.clipboard.writeText(url);
    setCopiedInviteUrl(invite.id);
    setTimeout(() => setCopiedInviteUrl(null), 2000);
  };

  // Toggle user active status
  const handleToggleActive = async (user) => {
    try {
      if (user.is_active) {
        await adminService.deactivateUser(user.id);
      } else {
        await adminService.activateUser(user.id);
      }
      await loadUsers();
      setSuccessMessage(`User ${user.username} ${user.is_active ? 'deactivated' : 'activated'}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      await adminService.createUser(userData);
      await loadUsers();
      setError('');
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditUser = async (userData) => {
    try {
      await adminService.updateUser(selectedUser.id, userData);
      await loadUsers();
      setError('');
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    try {
      await adminService.deleteUser(userToDelete.id);
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
      await loadUsers();
      setError('');
    } catch (err) {
      setError('Failed to delete user: ' + (err.response?.data?.detail || err.message));
      setIsDeleteDialogOpen(false);
    }
  };

  const openCreateModal = () => {
    setSelectedUser(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'staff':
        return 'bg-blue-100 text-blue-800';
      case 'client':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-virgo-navy-800 text-virgo-teal-100';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4" />;
      case 'staff':
        return <Users className="w-4 h-4" />;
      default:
        return <UserIcon className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-virgo-gold-400">Administration</h1>
        <p className="text-virgo-teal-300 mt-1">Manage system settings and data</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-virgo-teal-800 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'users'
                ? 'border-blue-500 text-virgo-teal-400'
                : 'border-transparent text-virgo-teal-400 hover:text-virgo-teal-200 hover:border-virgo-teal-700'
            }`}
          >
            <Users className="w-4 h-4" />
            User Management
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'import'
                ? 'border-blue-500 text-virgo-teal-400'
                : 'border-transparent text-virgo-teal-400 hover:text-virgo-teal-200 hover:border-virgo-teal-700'
            }`}
          >
            <Database className="w-4 h-4" />
            Data Import
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'analytics'
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-700 text-virgo-teal-200 hover:bg-virgo-navy-600'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Website Analytics</span>
          </button>
          <button
            onClick={() => setActiveTab('phone')}
            className={`flex items-center space-x-2 px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'phone'
                ? 'border-cyan-400 text-cyan-400'
                : 'border-transparent text-virgo-navy-200 hover:text-virgo-teal-300'
            }`}
          >
            <Phone className="w-4 h-4" />
            <span>Planchette</span>
          </button>
          <button
            onClick={() => setActiveTab('firm')}
            className={`flex items-center space-x-2 px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'firm'
                ? 'border-virgo-teal-400 text-virgo-teal-400'
                : 'border-transparent text-virgo-navy-200 hover:text-virgo-teal-300'
            }`}
          >
            <Building2 className="w-5 h-5" />
            <span>Firm Settings</span>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center space-x-2 px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'settings'
                ? 'border-virgo-teal-400 text-virgo-teal-400'
                : 'border-transparent text-virgo-navy-200 hover:text-virgo-teal-300'
            }`}
          >
            <Settings className="w-5 h-5" />
            <span>System Settings</span>
          </button>
          <button
            onClick={() => setActiveTab('ai-training')}
            className={`flex items-center space-x-2 px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'ai-training'
                ? 'border-cyan-400 text-cyan-400'
                : 'border-transparent text-virgo-navy-200 hover:text-virgo-teal-300'
            }`}
          >
            <Brain className="w-5 h-5" />
            <span>AI Training</span>
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`flex items-center space-x-2 px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'activity'
                ? 'border-green-400 text-green-400'
                : 'border-transparent text-virgo-navy-200 hover:text-virgo-teal-300'
            }`}
          >
            <Clock className="w-5 h-5" />
            <span>Activity Tracking</span>
          </button>
          <button
            onClick={() => setActiveTab('network')}
            className={`flex items-center space-x-2 px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'network'
                ? 'border-purple-400 text-purple-400'
                : 'border-transparent text-virgo-navy-200 hover:text-virgo-teal-300'
            }`}
          >
            <Network className="w-5 h-5" />
            <span>Network</span>
          </button>
          <button
            onClick={() => setActiveTab('court-records')}
            className={`flex items-center space-x-2 px-6 py-3 border-b-2 transition-colors ${
              activeTab === 'court-records'
                ? 'border-purple-400 text-purple-400'
                : 'border-transparent text-virgo-navy-200 hover:text-virgo-teal-300'
            }`}
          >
            <Landmark className="w-5 h-5" />
            <span>Court Records</span>
          </button>
        </nav>
      </div>

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <>
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-virgo-gold-400">Users</h2>
              <p className="text-virgo-teal-300 text-sm mt-1">Manage system users and permissions</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={openInviteModal}
                className="flex items-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 transition-colors"
              >
                <Mail className="w-5 h-5" />
                Invite User
              </button>
              <button
                onClick={openCreateModal}
                className="flex items-center gap-2 bg-virgo-teal-600 text-white px-4 py-2 rounded-lg hover:bg-virgo-teal-700 transition-colors"
              >
                <UserPlus className="w-5 h-5" />
                Create User
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <Check className="w-5 h-5" />
              {successMessage}
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-virgo-teal-400">Loading users...</div>
            </div>
          ) : (
            <div className="bg-virgo-navy-700 rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-virgo-navy-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      2FA
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-virgo-navy-700 divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-virgo-navy-600">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-virgo-navy-700 rounded-full flex items-center justify-center">
                            <UserIcon className="w-5 h-5 text-virgo-teal-400" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {user.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                          {getRoleIcon(user.role)}
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-virgo-navy-800 text-virgo-teal-100">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_2fa_enabled ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Enabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-virgo-navy-800 text-virgo-teal-100">
                            Disabled
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setManagedUserId(user.id)}
                          className="text-cyan-500 hover:text-cyan-400 mr-3"
                          title="Manage user"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openPasswordModal(user)}
                          className="text-yellow-500 hover:text-yellow-400 mr-3"
                          title="Reset password"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={`mr-3 ${user.is_active ? 'text-green-500 hover:text-red-400' : 'text-gray-500 hover:text-green-400'}`}
                          title={user.is_active ? 'Deactivate user' : 'Activate user'}
                        >
                          {user.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-virgo-teal-400 hover:text-blue-400 mr-3"
                          title="Quick edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteDialog(user)}
                          className="text-red-600 hover:text-red-400"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {users.length === 0 && !loading && (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-virgo-teal-500" />
                  <h3 className="mt-2 text-sm font-medium text-white">No users found</h3>
                  <p className="mt-1 text-sm text-virgo-teal-400">Get started by creating a new user.</p>
                </div>
              )}
            </div>
          )}

          {/* User Modal */}
          <UserModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={modalMode === 'create' ? handleCreateUser : handleEditUser}
            user={selectedUser}
            mode={modalMode}
          />

          {/* Delete Confirmation Dialog */}
          <ConfirmDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onConfirm={handleDeleteUser}
            title="Delete User"
            message={`Are you sure you want to delete ${userToDelete?.username}? This action cannot be undone.`}
            confirmText="Delete User"
            confirmStyle="danger"
          />

          {/* Password Reset Modal */}
          {isPasswordModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-virgo-navy-700 rounded-lg p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">Reset Password</h3>
                  <button onClick={() => setIsPasswordModalOpen(false)} className="text-gray-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-virgo-teal-300 mb-4">
                  Set a new password for <span className="font-semibold text-white">{userToReset?.username}</span>
                </p>
                {passwordError && (
                  <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-2 rounded-lg text-sm">
                    {passwordError}
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Confirm Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                      placeholder="Confirm new password"
                    />
                  </div>
                  <div className="text-xs text-virgo-teal-400">
                    Password must be at least 8 characters with uppercase, lowercase, and number.
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setIsPasswordModalOpen(false)}
                    className="px-4 py-2 bg-virgo-navy-600 text-white rounded-lg hover:bg-virgo-navy-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetPassword}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
                  >
                    <Key className="w-4 h-4" />
                    Reset Password
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Invite User Modal */}
          {isInviteModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-virgo-navy-700 rounded-lg p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">Invite User</h3>
                  <button onClick={() => setIsInviteModalOpen(false)} className="text-gray-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-virgo-teal-300 mb-4">
                  Send an invitation email to a new user. They'll receive a link to create their account.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      placeholder="user@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Role</label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="client">Client</option>
                      <option value="staff">Staff</option>
                      <option value="attorney">Attorney</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setIsInviteModalOpen(false)}
                    className="px-4 py-2 bg-virgo-navy-600 text-white rounded-lg hover:bg-virgo-navy-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendInvite}
                    disabled={!inviteEmail}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Send Invitation
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pending Invitations Section */}
          {invitations.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Pending Invitations ({invitations.length})
              </h3>
              <div className="bg-virgo-navy-700 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-virgo-navy-600">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Invited By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Expires</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-virgo-teal-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invitations.map((invite) => (
                      <tr key={invite.id} className="hover:bg-virgo-navy-600">
                        <td className="px-6 py-4 text-sm text-white">{invite.email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(invite.role)}`}>
                            {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-virgo-teal-300">{invite.invited_by}</td>
                        <td className="px-6 py-4 text-sm text-virgo-teal-400">
                          {new Date(invite.expires_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleResendInvitation(invite.id, invite.email)}
                            className="text-green-500 hover:text-green-400 mr-3"
                            title="Resend invitation"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRevokeInvitation(invite.id)}
                            className="text-red-500 hover:text-red-400 mr-3"
                            title="Revoke invitation"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Data Import Tab */}
      {activeTab === 'import' && (
        <Import />
      )}

      {/* Website Analytics Tab */}
      {activeTab === 'analytics' && (
        <WebsiteAnalytics />
      )}

      {/* RingCentral Integration Tab */}

      {/* Firm Settings Tab */}
      {activeTab === 'firm' && (
        <div className="space-y-6">
          {/* Success/Error Messages */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <Check className="w-5 h-5" />
              {successMessage}
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Office Hours Section */}
          <div className="bg-virgo-navy-700 rounded-lg p-6 border border-virgo-navy-600">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-virgo-teal-400 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Office Hours
                </h3>
                <p className="text-virgo-navy-100 text-sm mt-1">
                  Configure the firm's operating hours for each day of the week.
                </p>
              </div>
              <button
                onClick={saveOfficeHours}
                disabled={firmSettingsLoading}
                className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 disabled:opacity-50 flex items-center gap-2"
              >
                {firmSettingsLoading ? 'Saving...' : 'Save Hours'}
              </button>
            </div>

            <div className="space-y-3">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                <div key={day} className="flex items-center gap-4 p-4 bg-virgo-navy-800 rounded-lg border border-virgo-navy-600">
                  {/* Day Name */}
                  <div className="w-32">
                    <span className="text-white font-medium capitalize">{day}</span>
                  </div>

                  {/* Open/Closed Toggle */}
                  <button
                    onClick={() => handleOfficeHoursChange(day, 'is_open', !officeHours[day]?.is_open)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      officeHours[day]?.is_open ? 'bg-virgo-teal-500' : 'bg-virgo-navy-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        officeHours[day]?.is_open ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`w-16 text-sm ${officeHours[day]?.is_open ? 'text-virgo-teal-400' : 'text-virgo-navy-300'}`}>
                    {officeHours[day]?.is_open ? 'Open' : 'Closed'}
                  </span>

                  {/* Time Inputs */}
                  {officeHours[day]?.is_open && (
                    <>
                      <div className="flex items-center gap-2">
                        <label className="text-virgo-navy-200 text-sm">From:</label>
                        <input
                          type="time"
                          value={officeHours[day]?.open || '08:00'}
                          onChange={(e) => handleOfficeHoursChange(day, 'open', e.target.value)}
                          className="bg-virgo-navy-700 border border-virgo-navy-500 rounded px-3 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-virgo-navy-200 text-sm">To:</label>
                        <input
                          type="time"
                          value={officeHours[day]?.close || '17:00'}
                          onChange={(e) => handleOfficeHoursChange(day, 'close', e.target.value)}
                          className="bg-virgo-navy-700 border border-virgo-navy-500 rounded px-3 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-virgo-navy-800 rounded border border-virgo-teal-500/30">
              <p className="text-virgo-navy-100 text-sm">
                <strong className="text-virgo-teal-400">Note:</strong> Office hours are displayed in Eastern Time (America/New_York).
                These settings affect the client portal office status indicator and automated responses.
              </p>
            </div>
          </div>

          {/* Away Message Section */}
          <div className="bg-virgo-navy-700 rounded-lg p-6 border border-virgo-navy-600">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-virgo-teal-400 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Away Message (After Hours)
                </h3>
                <p className="text-virgo-navy-100 text-sm mt-1">
                  Automatically respond to emails received outside of office hours.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${awayMessage.enabled ? 'text-virgo-teal-400' : 'text-virgo-navy-300'}`}>
                    {awayMessage.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    onClick={() => handleAwayMessageChange('enabled', !awayMessage.enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      awayMessage.enabled ? 'bg-virgo-teal-500' : 'bg-virgo-navy-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        awayMessage.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <button
                  onClick={saveAwayMessage}
                  disabled={firmSettingsLoading}
                  className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {firmSettingsLoading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Subject Line */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Email Subject</label>
                <input
                  type="text"
                  value={awayMessage.subject}
                  onChange={(e) => handleAwayMessageChange('subject', e.target.value)}
                  className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                  placeholder="Thank you for contacting us"
                />
              </div>

              {/* Message Body */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Message Body</label>
                <textarea
                  value={awayMessage.message}
                  onChange={(e) => handleAwayMessageChange('message', e.target.value)}
                  rows={6}
                  className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 resize-y"
                  placeholder="Thank you for your email. Our office is currently closed..."
                />
              </div>

              {/* Cooldown Hours */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">Reply Cooldown</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={awayMessage.cooldown_hours}
                      onChange={(e) => handleAwayMessageChange('cooldown_hours', parseInt(e.target.value) || 24)}
                      className="w-24 bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                    />
                    <span className="text-virgo-navy-200 text-sm">hours before replying to the same sender again</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-virgo-navy-800 rounded border border-yellow-500/30">
              <p className="text-virgo-navy-100 text-sm">
                <strong className="text-yellow-400">How it works:</strong> When enabled, Astraea will automatically send a response
                to any email received outside of office hours. The system will not reply to the same sender within the cooldown period,
                and will skip emails from noreply addresses and internal domains.
              </p>
            </div>
          </div>

          {/* Intake Settings Section */}
          <div className="bg-virgo-navy-700 rounded-lg p-6 border border-virgo-navy-600">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-virgo-teal-400 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Intake Settings
                </h3>
                <p className="text-virgo-navy-100 text-sm mt-1">
                  Configure default settings for the client intake process.
                </p>
              </div>
              <button
                onClick={saveIntakeSettings}
                disabled={firmSettingsLoading}
                className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 disabled:opacity-50 flex items-center gap-2"
              >
                {firmSettingsLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            <div className="space-y-4">
              {/* Default Consultation Fee */}
              <div className="p-4 bg-virgo-navy-800 rounded-lg border border-virgo-navy-600">
                <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                  Default Consultation Fee
                </label>
                <p className="text-virgo-navy-200 text-sm mb-3">
                  The default fee charged for initial consultations. This can be adjusted per intake.
                </p>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-virgo-teal-400">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={(intakeSettings.default_consult_fee / 100).toFixed(2)}
                      onChange={(e) => handleIntakeSettingsChange('default_consult_fee', Math.round(parseFloat(e.target.value) * 100) || 0)}
                      className="w-32 pl-8 pr-3 py-2 bg-virgo-navy-700 border border-virgo-navy-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                    />
                  </div>
                  <span className="text-virgo-navy-200 text-sm">USD</span>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-virgo-navy-800 rounded border border-virgo-teal-500/30">
              <p className="text-virgo-navy-100 text-sm">
                <strong className="text-virgo-teal-400">Note:</strong> This default fee will be used when creating new Quick Intakes.
                Staff can override this amount on a per-intake basis, or waive the fee entirely.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-virgo-navy-700 rounded-lg p-6 border border-virgo-navy-600">
            <h3 className="text-lg font-semibold text-virgo-teal-400 mb-4">Communication System Modes</h3>
            <p className="text-virgo-navy-100 text-sm mb-6">
              Configure whether VIRGO manages communications centrally (Server Mode) or syncs with external providers (Client Mode).
              All data persists in VIRGO regardless of mode.
            </p>
            
            <div className="space-y-6">
              {/* Mail Mode Toggle */}
              <div className="flex items-center justify-between p-4 bg-virgo-navy-800 rounded-lg border border-virgo-navy-600">
                <div className="flex-1">
                  <h4 className="text-virgo-teal-400 font-medium mb-1">Email System Mode</h4>
                  <p className="text-virgo-navy-200 text-sm">
                    <span className="font-semibold">Server Mode:</span> VIRGO manages email centrally
                    <br />
                    <span className="font-semibold">Client Mode:</span> Users connect external email accounts (Gmail, Outlook, etc.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className={`text-sm font-medium ${systemSettings.mail_mode === 'server' ? 'text-virgo-teal-400' : 'text-virgo-navy-300'}`}>
                    Server
                  </span>
                  <button
                    onClick={() => updateSystemSetting('mail_mode', systemSettings.mail_mode === 'server' ? 'client' : 'server')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      systemSettings.mail_mode === 'client' ? 'bg-virgo-teal-500' : 'bg-virgo-navy-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        systemSettings.mail_mode === 'client' ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${systemSettings.mail_mode === 'client' ? 'text-virgo-teal-400' : 'text-virgo-navy-300'}`}>
                    Client
                  </span>
                </div>
              </div>

              {/* Calendar Mode Toggle */}
              <div className="flex items-center justify-between p-4 bg-virgo-navy-800 rounded-lg border border-virgo-navy-600">
                <div className="flex-1">
                  <h4 className="text-virgo-teal-400 font-medium mb-1">Calendar System Mode</h4>
                  <p className="text-virgo-navy-200 text-sm">
                    <span className="font-semibold">Server Mode:</span> VIRGO manages calendar centrally
                    <br />
                    <span className="font-semibold">Client Mode:</span> Users connect external calendars (Google Calendar, Outlook, etc.)
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className={`text-sm font-medium ${systemSettings.calendar_mode === 'server' ? 'text-virgo-teal-400' : 'text-virgo-navy-300'}`}>
                    Server
                  </span>
                  <button
                    onClick={() => updateSystemSetting('calendar_mode', systemSettings.calendar_mode === 'server' ? 'client' : 'server')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      systemSettings.calendar_mode === 'client' ? 'bg-virgo-teal-500' : 'bg-virgo-navy-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        systemSettings.calendar_mode === 'client' ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${systemSettings.calendar_mode === 'client' ? 'text-virgo-teal-400' : 'text-virgo-navy-300'}`}>
                    Client
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-virgo-navy-800 rounded border border-virgo-teal-500/30">
              <p className="text-virgo-navy-100 text-sm">
                <strong className="text-virgo-teal-400">Note:</strong> Switching modes does not delete existing data. 
                Messages, calendar events, and folders persist across modes. The mode only determines synchronization behavior.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'phone' && (
        <div className="space-y-6">
          {/* Phone provider sub-tabs */}
          <div className="border-b border-virgo-navy-600">
            <nav className="flex space-x-1">
              {[
                { id: 'telnyx', label: 'Telnyx (Active)' },
                { id: 'ringcentral', label: 'RingCentral (Legacy)' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setPhoneSubTab(tab.id)}
                  className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                    phoneSubTab === tab.id
                      ? 'border-cyan-500 text-cyan-400'
                      : 'border-transparent text-virgo-teal-500 hover:text-virgo-teal-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          {phoneSubTab === 'ringcentral' && <RingCentralIntegration />}
          {phoneSubTab === 'telnyx' && <TelnyxConfig />}
        </div>
      )}

      {/* AI Training Tab */}
      {activeTab === 'ai-training' && (
        <AITrainingPanel />
      )}

      {/* Activity Tracking Tab */}
      {activeTab === 'activity' && (
        <ActivityTrackingPanel />
      )}

      {activeTab === 'network' && (
        <MeshNetworkPanel />
      )}

      {activeTab === 'court-records' && (
        <CourtRecordsAdmin />
      )}

      {/* User Management Panel Slideout */}
      {managedUserId && (
        <UserManagementPanel
          userId={managedUserId}
          onClose={() => setManagedUserId(null)}
          onUpdate={loadUsers}
        />
      )}
    </div>
  );
}

export default AdminPanel;

